import { Component, ElementRef, EventEmitter, Input, OnInit, Output, Renderer2, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, first, repeatWhen, takeUntil } from 'rxjs/operators';
import { AutoSub, AutoSubOnDestroy, ToastService } from '../../providers';

export interface ValueChanged {
    id: string;
    value: unknown;
    meta?: unknown;
}

@AutoSubOnDestroy()
@Component({
    selector: 'input-form-field',
    templateUrl: './input-form-field.component.html',
    styleUrls: ['./input-form-field.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class InputFormFieldComponent implements OnInit {

    @Input() id: string;
    @Input() title: string;
    @Input() description: string;
    @Input() meta: unknown;
    @Input() debounceTime: number = 1000;
    @Input() type: string;
    @Input('control') set setControl(control: FormControl) {
        if(this.isFocused) return;

        this.control = control;
        this.lastEmittedValue = control?.value;
        this.handleControlChange();
    }

    @Input('input') set setInput(input: string) {
        if(this.isFocused) return;

        this.input = input;
        this.lastEmittedValue = input;
    }
    @Input() fieldType: 'inputForm' | 'inputModel' | 'textarea' | 'number' = 'inputForm';
    @Input() placeholder: string = '';

    /** Initial number of textarea lines (grows on overflow) */
    @Input() rows: number = 1;
    /** Set true to limit number of characters in input (524288 = default limit: https://www.w3schools.com/tags/att_input_maxlength.asp) */
    @Input() characterLimit: number = 524288;
    /** Set true to prevent input changes and gray out */
    @Input() disabled: boolean = false;
    /** Set true to add copy-SVG and prevent input changes */
    @Input() isCopyField: boolean = false;
    /** Set to an SVG name to add as left-side button */
    @Input() customButton: string = null;
    /** Set true to hide character limit in field */
    @Input() hideCharacterLimit: boolean = false;
    /** Min number for 'number' inputs */
    @Input() minNumber: number = 0;
    /** Max number for 'number' inputs */
    @Input() maxNumber: number = 524288;
    @Input() error: boolean = false;

    @Output() inputChange = new EventEmitter<string>();
    @Output() valueChanged = new EventEmitter<ValueChanged>();
    @Output() customButtonClicked = new EventEmitter<void>();
    @Output() focus = new EventEmitter();

    @ViewChild('textarea') textarea: ElementRef<HTMLTextAreaElement>;
    @ViewChild('inputmodel') inputModel: ElementRef<HTMLInputElement>;
    @ViewChild('inputfield') inputField: ElementRef<HTMLInputElement>;
    @ViewChild('inputcontainer') inputContainer: ElementRef<HTMLDivElement>;

    private stop = new Subject<boolean>();
    private start = new Subject<boolean>();
    private initSubject = new ReplaySubject();

    public control: FormControl;
    public input: string = ''; // For ngModel input
    public highlightCharacterCount = false;
    public isFocused: boolean = false;
    /** Last value emitted via valueChanged; used to flush on blur without double-saving. */
    private lastEmittedValue: unknown;

    constructor(
        private renderer: Renderer2,
        private toastService: ToastService
    ) { }

    ngOnInit(): void { }

    ngAfterViewInit(): void {
        this.initSubject.next(true);
    }

    private async handleControlChange() {
        this.stop.next(true);
        await this.initSubject.pipe(first()).toPromise();
        this.resizeTextArea();

        AutoSub(this)['controlSub'] = this.control.valueChanges.pipe(
            debounceTime(this.debounceTime),
            takeUntil(this.stop),
            repeatWhen(() => this.start),
        ).subscribe((value) => {
            this.emitChange(value);
        });
    }

    public startSave() { this.start.next(true); }
    public stopSave() { this.start.next(true); }

    public emitChange(value) {
        if(typeof value == 'string') value = value.slice(0, this.characterLimit);

        if(value === this.lastEmittedValue) return;
        this.lastEmittedValue = value;

        if(this.input) {
            this.input = value;
        }

        this.inputChange.emit(value);
        this.valueChanged.emit({
            id: this.id,
            meta: this.meta,
            value: value,
        });
    }

    public resizeTextArea() {
        if(!this.textarea) return;

        const textarea = this.textarea.nativeElement as HTMLTextAreaElement;
        const borderHeight = textarea.offsetHeight - textarea.clientHeight;

        this.setHeight(this.inputContainer, `${textarea.scrollHeight + borderHeight}px`);
        this.setHeight(this.textarea, 'auto');
        this.setHeight(this.textarea, `${Math.max(textarea.scrollHeight + borderHeight, 43)}px`);
        this.setHeight(this.inputContainer, 'auto');

    }

    private setHeight(ref: ElementRef, value: string) {
        this.renderer.setStyle(ref.nativeElement, 'height', value);
    }

    public addCopyToast() {
        this.toastService.addToast($localize`Copied to clipboard`);
    }

    public selectAll() {
        if(!this.isCopyField || this.inputModel) return;
        this.inputModel.nativeElement.select();
    }

    public highlightCharacterLimit(event: KeyboardEvent) {
        if(this.hasSelection()) return; // Since something is selected and will be overwritten, we allow this.

        const isPrintable = event?.key?.length == 1;
        if(!isPrintable && event.key != 'Enter') return; // Return if not printable character (e.g. escape, ctrl)

        const inputValueLength = this.input?.length ?? 0;
        const controlValueLength = (this.control?.value.length ?? 0) as number;
        const inputLength = Math.max(inputValueLength, controlValueLength);

        if(inputLength < this.characterLimit) return; // Return if limit not yet reached

        this.highlightCharacterCount = true;
        setTimeout(() => this.highlightCharacterCount = false, 500);
    }

    private hasSelection(): boolean {
        // Get active input field. All except one of these are (or should be) null
        const inputfield = this.textarea ?? this.inputModel ?? this.inputField ?? null;
        if(!inputfield) {
            console.error('No input field found');
            return false;
        }

        // If "start" and "end" are the same, it means that no range has been selected and the cursor
        // is in a single space somewhere in the string
        return inputfield.nativeElement.selectionStart != inputfield.nativeElement.selectionEnd;
    }

    public handleCharacterLimit() {
        const inputValueLength = this.input?.length ?? 0;
        const controlValueLength = (this.control?.value.length ?? 0) as number;
        const inputLength = Math.max(inputValueLength, controlValueLength);

        const hasSelection = this.hasSelection();
        if(hasSelection) return true;

        return inputLength < this.characterLimit;
    }

    public customButtonClick(ev: MouseEvent) {
        this.customButtonClicked.emit();
        // Make sure input field will not focus, preventing programmatic control changes
        ev.preventDefault();
    }

    public onFocusHandler() {
        this.isFocused = true;
        this.focus.emit();
    }

    public onBlur() {
        this.isFocused = false;
        this.flushPendingChange();
    }

    /** Commit any value that left the field before the debounce fired. */
    private flushPendingChange() {
        const value = this.control !== undefined ? this.control.value : this.input;
        if(value === this.lastEmittedValue) return;
        this.emitChange(value);
    }
}
